import { useCallback, useEffect, useRef } from 'react';
import axis from '../constants/axis';

const AXIS_SWITCH_THRESHOLD = 10; // px, to avoid jitter when deltas are close

/**
 * Hook to handle axis locking functionality when Shift key is pressed
 * Provides axis determination and Shift key state management
 *
 * @param {Object} config Configuration object
 * @param {boolean} config.locked Whether axis locking is currently active (Shift pressed)
 * @returns {Object} Object containing axis locking utilities
 */
const useAxisLocker = ({ locked }) => {
  const lockedAxisRef = useRef(null);

  // Reset locked axis when locking is disabled
  useEffect(() => {
    if (!locked) {
      lockedAxisRef.current = null;
    }
  }, [locked]);

  /**
   * Determines which axis to lock based on movement distance and current lock state
   *
   * @param {number[]} distance - [dx, dy] movement distance
   * @param {string|null} currentLockedAxis - Currently locked axis ('x', 'y', or null)
   * @returns {string} The axis to lock ('x' or 'y')
   */
  const determineLockedAxis = useCallback((distance, currentLockedAxis) => {
    const [dx, dy] = distance;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);

    // If no axis is currently locked, determine based on dominant movement
    if (!currentLockedAxis) {
      const deltaX = absX - absY;
      const deltaY = absY - absX;

      // Use threshold to avoid jitter on near-diagonal movements
      if (deltaX > AXIS_SWITCH_THRESHOLD) {
        return axis.X;
      }

      if (deltaY > AXIS_SWITCH_THRESHOLD) {
        return axis.Y;
      }

      // Default to the axis with larger absolute movement
      return absX >= absY ? axis.X : axis.Y;
    }

    // Check if user wants to switch axes (significant movement in other direction)
    const isXLocked = currentLockedAxis === axis.X;
    const isYLocked = currentLockedAxis === axis.Y;

    if (isXLocked && absY - absX > AXIS_SWITCH_THRESHOLD) {
      return axis.Y;
    }

    if (isYLocked && absX - absY > AXIS_SWITCH_THRESHOLD) {
      return axis.X;
    }

    // Keep current lock
    return currentLockedAxis;
  }, []);

  /**
   * Computes movement distance with axis locking applied
   *
   * @param {number[]} distance - [dx, dy] raw movement distance
   * @returns {Object} Object with locked distance and axis information
   */
  const computeDistanceWithLock = useCallback(
    (distance) => {
      if (!locked) {
        return {
          lockedAxis: null,
          distance,
        };
      }

      const [dx, dy] = distance;
      const newLockedAxis = determineLockedAxis(distance, lockedAxisRef.current);

      // Update the ref with the new locked axis
      lockedAxisRef.current = newLockedAxis;

      // Apply axis locking by zeroing out the non-locked axis
      const lockedDistance = newLockedAxis === axis.X ? [dx, 0] : [0, dy];

      return {
        distance: lockedDistance,
        lockedAxis: newLockedAxis,
      };
    },
    [determineLockedAxis, locked]
  );

  return {
    computeDistanceWithLock,
  };
};

export default useAxisLocker;
